import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    // Verificar se há um usuário na sessão
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        setUserEmail(session.user.email || null);
        
        // Verificar se email já foi confirmado
        if (session.user.email_confirmed_at) {
          setIsVerified(true);
          // Redirecionar após 2 segundos
          setTimeout(() => {
            navigate("/onboarding/personal");
          }, 2000);
        }
      } else {
        // Sem sessão, voltar para registro
        navigate("/register");
      }
    };

    checkSession();

    // Escutar mudanças de auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user?.email_confirmed_at) {
        setIsVerified(true);
        setTimeout(() => {
          navigate("/onboarding/personal");
        }, 2000);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleResendEmail = async () => {
    if (!userEmail) return;

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: userEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=/onboarding/personal`,
        },
      });

      if (error) throw error;

      toast({
        title: "Email reenviado",
        description: "Verifique sua caixa de entrada e spam.",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao reenviar",
        description: error.message || "Tente novamente em alguns minutos.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  // Tela de verificação concluída
  if (isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">Email Verificado!</CardTitle>
            <CardDescription className="text-base mt-2">
              Sua conta foi confirmada com sucesso.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">
              Redirecionando para o próximo passo...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Verifique seu email</CardTitle>
          <CardDescription className="text-base mt-2">
            Enviamos um link de confirmação para:
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <p className="font-medium text-lg text-primary">
              {userEmail || "Carregando..."}
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
            <p className="font-medium">Próximos passos:</p>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Abra seu email</li>
              <li>Clique no link de confirmação</li>
              <li>Complete seu cadastro</li>
            </ol>
          </div>

          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Não recebeu o email? Verifique a pasta de spam.
            </p>
            <Button
              variant="outline"
              onClick={handleResendEmail}
              disabled={isResending}
              className="gap-2"
            >
              {isResending ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Reenviando...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Reenviar email
                </>
              )}
            </Button>
          </div>

          <div className="text-center">
            <Link to="/auth" className="text-sm text-muted-foreground hover:text-primary">
              ← Voltar para login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
